import React from 'react';
import Icon from '../../../components/AppIcon';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const ArtistAnalytics = ({ analytics }) => {
  const engagementData = [
    { month: 'Jan', views: 1200, interactions: 340 },
    { month: 'Feb', views: 1800, interactions: 520 },
    { month: 'Mar', views: 2400, interactions: 680 },
    { month: 'Apr', views: 2100, interactions: 590 },
    { month: 'May', views: 2800, interactions: 750 },
    { month: 'Jun', views: 3200, interactions: 890 }
  ];

  const contentQualityData = [
    { section: 'Profile', score: 95 },
    { section: 'About', score: 88 },
    { section: 'Calendar', score: 92 },
    { section: 'Work', score: 85 },
    { section: 'Shop', score: 78 },
    { section: 'Promotion', score: 90 }
  ];

  return (
    <div className="bg-surface border border-border rounded-lg p-6 elevation-1">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-text-primary">Artist Analytics</h2>
        <div className="flex items-center space-x-2 text-sm text-text-secondary">
          <Icon name="TrendingUp" size={16} />
          <span>Last 30 days</span>
        </div>
      </div>
      {/* Key Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Eye" size={16} className="text-accent" />
            <span className="text-sm text-text-secondary">Profile Views</span>
          </div>
          <div className="text-2xl font-semibold text-text-primary">{analytics?.profileViews?.toLocaleString()}</div>
          <div className="flex items-center space-x-1 text-sm text-success">
            <Icon name="TrendingUp" size={12} />
            <span>+{analytics?.viewsGrowth}%</span>
          </div>
        </div>

        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Users" size={16} className="text-accent" />
            <span className="text-sm text-text-secondary">Unique Visitors</span>
          </div>
          <div className="text-2xl font-semibold text-text-primary">{analytics?.uniqueVisitors?.toLocaleString()}</div>
          <div className="flex items-center space-x-1 text-sm text-success">
            <Icon name="TrendingUp" size={12} />
            <span>+{analytics?.visitorsGrowth}%</span>
          </div>
        </div>

        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="Heart" size={16} className="text-accent" />
            <span className="text-sm text-text-secondary">Engagement</span>
          </div>
          <div className="text-2xl font-semibold text-text-primary">{analytics?.engagementRate}%</div>
          <div className="flex items-center space-x-1 text-sm text-warning">
            <Icon name="Minus" size={12} />
            <span>-{analytics?.engagementChange}%</span>
          </div>
        </div>

        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="CheckCircle" size={16} className="text-accent" />
            <span className="text-sm text-text-secondary">Completion</span>
          </div>
          <div className="text-2xl font-semibold text-text-primary">{analytics?.overallCompletion}%</div>
          <div className="flex items-center space-x-1 text-sm text-success">
            <Icon name="TrendingUp" size={12} />
            <span>+{analytics?.completionGrowth}%</span>
          </div>
        </div>
      </div>
      {/* Engagement Chart */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-text-primary mb-4">Mobile App Engagement</h3>
        <div className="w-full h-48">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={engagementData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="month" stroke="var(--color-text-secondary)" fontSize={12} />
              <YAxis stroke="var(--color-text-secondary)" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--color-surface)', 
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }}
              />
              <Line type="monotone" dataKey="views" stroke="var(--color-accent)" strokeWidth={2} />
              <Line type="monotone" dataKey="interactions" stroke="var(--color-success)" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Content Quality Scores */}
      <div className="mb-6">
        <h3 className="text-sm font-medium text-text-primary mb-4">Content Quality Scores</h3>
        <div className="w-full h-48">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={contentQualityData}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
              <XAxis dataKey="section" stroke="var(--color-text-secondary)" fontSize={12} />
              <YAxis stroke="var(--color-text-secondary)" fontSize={12} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'var(--color-surface)', 
                  border: '1px solid var(--color-border)',
                  borderRadius: '8px'
                }}
              />
              <Bar dataKey="score" fill="var(--color-accent)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Recent Activity */}
      <div>
        <h3 className="text-sm font-medium text-text-primary mb-4">Recent Activity</h3>
        <div className="space-y-3">
          {analytics?.recentActivity?.map((activity, index) => (
            <div key={index} className="flex items-start space-x-3 p-3 bg-muted rounded-lg">
              <div className="w-8 h-8 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name={activity?.icon} size={14} className="text-accent" />
              </div>
              <div className="flex-1">
                <p className="text-sm text-text-primary">{activity?.description}</p>
                <div className="flex items-center space-x-2 mt-1">
                  <span className="text-xs text-text-secondary">{activity?.timestamp}</span>
                  <span className="text-xs text-accent">{activity?.section}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ArtistAnalytics;